import { z } from '@/utils/zod'
import { OrganizationEntitySchema } from '@/entities/Entity'

import { TaxSchema } from './Tax'
import { AccountRangeSchema, AccountCategorySchema, AccountSchema } from './Account'
import { CostCenterRegionSchema } from './CostCenter'
import { AccountingStatementTemplateSchema } from './AccountingStatement'

export const accountingPlanTypes = [
    'TEST',
    'SKR42EUR',
    'SKR42GUV',
    'SKR03GUV',
    'SKR04GUV',
    'SKR07EAR',
    'SKR07GUV',
] as const

export const AccountingPlanTypeSchema = z.enum(accountingPlanTypes)

export type AccountingPlanType = z.infer<typeof AccountingPlanTypeSchema>

export const accountingPlanSupportsDepositsClearingAccount = (baseType: AccountingPlanType) => baseType.endsWith('GUV')

export const MAX_ACCOUNTING_ACCOUNT_DIGITS = 8

//
// !! Take care
// Adding new account number references here ensure to
// - return it within the getAllSettingsAccounts function too
// - handle it updateAccountingPlanSettingsAccountDigits endpoint
//
export const AccountingPlanSettingsSchema = z.object({
    accountDigits: z.number().int().min(1).max(MAX_ACCOUNTING_ACCOUNT_DIGITS),
    accountDigitsSplitLast: z.boolean(),
    cashRegisterRange: z.array(AccountSchema.shape.number).min(2).max(2),
    cashAccountRange: z.array(AccountSchema.shape.number).min(2).max(2),
    debtorCreditorClearingAccount: AccountSchema.shape.number.nullable(),
    depositsClearingAccount: AccountSchema.shape.number.nullable(),
    debtorCollectingAccount: AccountSchema.shape.number,
    creditorCollectingAccount: AccountSchema.shape.number,
    debtorInterestAccount: AccountSchema.shape.number,
    creditorInterestAccount: AccountSchema.shape.number,
    debtorForwardingAccount: AccountSchema.shape.number,
    creditorForwardingAccount: AccountSchema.shape.number,
    forwardingAccount: AccountSchema.shape.number,
    forwardingRange: z.array(AccountSchema.shape.number).min(2).max(2),
    defaultRevenueAccount: AccountSchema.shape.number,
    defaultExpenseAccount: AccountSchema.shape.number,
    moneyTransactionCosts: z.object({
        account: AccountSchema.shape.number,
        preTaxCode: TaxSchema.shape.code,
    }),
    transitAccount: AccountSchema.shape.number,
    transitAccountRange: z.array(AccountSchema.shape.number).min(2).max(2),
    moneyTransitAccount: AccountSchema.shape.number,
    depreciationAccounts: z.object({
        debit: AccountSchema.shape.number,
        credit: AccountSchema.shape.number,
    }),
    debtorAccountRange: z.array(AccountSchema.shape.number).min(2).max(2),
    creditorAccountRange: z.array(AccountSchema.shape.number).min(2).max(2),
    defaultEventRateAccount: AccountSchema.shape.number,
    defaultResourceRateAccount: AccountSchema.shape.number,
    defaultPunchCardAccount: AccountSchema.shape.number,
    defaultBookingServiceAccount: AccountSchema.shape.number,
    defaultPlanAccount: AccountSchema.shape.number,
    // Compensation claims book hourly and flat rates plus corrections, mileage and expenses on these accounts, and
    // the parts contributors waive as donations on the waiver account
    defaultCompensationAccounts: z.object({
        compensation: AccountSchema.shape.number,
        mileage: AccountSchema.shape.number,
        expenses: AccountSchema.shape.number,
        waiver: AccountSchema.shape.number,
    }),
    deductibleExpenseTaxAccounts: z
        .object({
            counterAccount: AccountSchema.shape.number,
            reversalAccount: AccountSchema.shape.number,
        })
        .nullable(),
})

export type AccountingPlanSettings = z.infer<typeof AccountingPlanSettingsSchema>

export const AccountingPlanSchema = OrganizationEntitySchema.extend({
    baseType: AccountingPlanTypeSchema,
    settings: AccountingPlanSettingsSchema,
    taxes: z.array(TaxSchema),
    regions: z.array(CostCenterRegionSchema),
    categories: z.array(AccountCategorySchema),
    ranges: z.array(AccountRangeSchema),
    accounts: z.array(AccountSchema),
    incomeStatement: AccountingStatementTemplateSchema,
})

export type AccountingPlan = z.infer<typeof AccountingPlanSchema>
